import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { ChangeEventHandler, memo, useCallback, useContext, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/Button';
import { Input } from '../../components/inputs/Input';
import { UserContext } from '../../contexts/UserContext';
import { ToastType, useToastNotifications } from '../../hooks/useToastNotifications';
import { T, useT } from '../../translation/src';
import { DebugRunDailyJobMutation, useDebugMakeOdooOrderDueMutation, useDebugRunDailyJobMutation, useDebugRunOdooCronMutation, useDebugToolsQuery } from '../../types/graphqlTypes';
import { AdminSectionLinks } from './AdminSectionLinks';

const DAY_MS = 24 * 60 * 60 * 1000;

// The job runs on UTC days: the picker shows and takes UTC.
const toInputValue = (time: number) => new Date(time).toISOString().slice(0, 16);
const fromInputValue = (value: string) => Date.parse(`${value}:00.000Z`);
const addMonths = (time: number, months: number) => {
  const date = new Date(time);
  date.setUTCMonth(date.getUTCMonth() + months);
  return date.getTime();
};
// Five past midnight UTC on the first of the next month: when the cron combines partners' orders.
const nextMonthStart = (time: number) => {
  const date = new Date(time);
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 1, 0, 5);
};

const Section: React.FC<{ title: React.ReactNode; children: React.ReactNode }> = ({ title, children }) => (
  <div className="flex flex-col gap-3">
    <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">{title}</span>
    {children}
  </div>
);

type StepResult = DebugRunDailyJobMutation['debugRunDailyJob'][number];

// Superadmins, on a developer's machine only (the server's DEBUG-TOOLS, never in production): the daily job run as if
// it were another day, step by step or as a whole; Odoo's own subscription jobs run now; an organisation's Odoo
// subscription made due today, so Odoo's recurring invoice job renews it.
const DebugTools_: React.FC = () => {
  const currentUser = useContext(UserContext);
  const { data, loading, refetch } = useDebugToolsQuery({ skip: !currentUser?.isSuperAdmin, fetchPolicy: 'network-only' });
  const tools = data?.debugTools;
  const [runDailyJob, { loading: running }] = useDebugRunDailyJobMutation();
  const [runOdooCron] = useDebugRunOdooCronMutation();
  const [makeDue, { loading: makingDue }] = useDebugMakeOdooOrderDueMutation();
  const { showToast } = useToastNotifications();
  const failed = useT('failed', { swc: true });
  const done = useT('done', { swc: true });

  const [now, setNow] = useState(() => Date.now());
  const [unchecked, setUnchecked] = useState<string[]>([]);
  const [results, setResults] = useState<StepResult[]>([]);
  const [organisationId, setOrganisationId] = useState('');
  const [runningCron, setRunningCron] = useState<number | null>(null);
  const steps = useMemo(() => tools?.steps ?? [], [tools]);
  const chosen = steps.filter((step) => !unchecked.includes(step.key)).map((step) => step.key);

  const fail = useCallback((e: unknown) => showToast({ title: failed, text: e instanceof Error ? e.message : String(e), type: ToastType.ERROR }), [failed, showToast]);
  const onChangeNow: ChangeEventHandler<HTMLInputElement> = useCallback((event) => {
    const time = fromInputValue(event.target.value);
    if (Number.isFinite(time)) setNow(time);
  }, []);
  const toggle = useCallback((key: string) => setUnchecked((current) => (current.includes(key) ? current.filter((one) => one !== key) : [...current, key])), []);

  const onRun = useCallback(async () => {
    try {
      const result = await runDailyJob({ variables: { now, steps: chosen.length === steps.length ? null : chosen } });
      setResults(result.data?.debugRunDailyJob ?? []);
    } catch (e) {
      fail(e);
    }
  }, [chosen, fail, now, runDailyJob, steps.length]);
  const onRunCron = useCallback(
    async (cronId: number) => {
      setRunningCron(cronId);
      try {
        await runOdooCron({ variables: { cronId } });
        showToast({ title: done, text: '', type: ToastType.SUCCESS });
        await refetch();
      } catch (e) {
        fail(e);
      } finally {
        setRunningCron(null);
      }
    },
    [done, fail, refetch, runOdooCron, showToast],
  );
  const onMakeDue = useCallback(async () => {
    try {
      await makeDue({ variables: { organisationId: organisationId.trim() } });
      showToast({ title: done, text: '', type: ToastType.SUCCESS });
    } catch (e) {
      fail(e);
    }
  }, [done, fail, makeDue, organisationId, showToast]);

  if (!currentUser?.isSuperAdmin) return null;
  return (
    <div className="flex flex-col w-full h-full px-14 overflow-auto pb-8 dark:bg-[#313131] dark:text-white">
      <div className="flex items-center gap-3 w-full pt-8 text-xl">
        <Link to="/admin-center" className="text-gray-500 hover:text-black dark:text-gray-400 dark:hover:text-white" aria-label="admin center">
          <ArrowLeftIcon className="w-5 h-5" />
        </Link>
        <T _str="debug tools" swc />
        <AdminSectionLinks />
      </div>
      <hr className="my-4 border-t border-gray-300 dark:border-gray-400" />
      {loading && !tools ? (
        <span className="text-sm text-gray-500">
          <T _str="loading..." />
        </span>
      ) : !tools?.enabled ? (
        <span className="text-sm text-gray-500">
          <T _str="The debug tools are off. Set DEBUG-TOOLS=true on the server to use them. They never run in production." />
        </span>
      ) : (
        <div className="flex flex-col gap-10 max-w-4xl">
          <Section title={<T _str="daily job" swc />}>
            <span className="text-xs text-gray-500">
              <T _str="Runs the nightly job as if it were this moment. Only the job uses this date. Rows it writes keep it, so the database moves into that future." />
            </span>
            <div className="flex flex-wrap items-center gap-2">
              <Input type="datetime-local" className="w-56" value={toInputValue(now)} onChange={onChangeNow} />
              <span className="text-xs text-gray-500">UTC</span>
              <Button variant="tertiary" size="xs" onClick={() => setNow(Date.now())}>
                <T _str="now" swc />
              </Button>
              <Button variant="tertiary" size="xs" onClick={() => setNow((time) => time + DAY_MS)}>
                +1 <T _str="day" />
              </Button>
              <Button variant="tertiary" size="xs" onClick={() => setNow((time) => addMonths(time, 1))}>
                +1 <T _str="month" />
              </Button>
              <Button variant="tertiary" size="xs" onClick={() => setNow((time) => addMonths(time, 12))}>
                +1 <T _str="year" />
              </Button>
              <Button variant="tertiary" size="xs" onClick={() => setNow((time) => nextMonthStart(time))}>
                <T _str="next month's first night" swc />
              </Button>
            </div>
            <div className="flex flex-col gap-1">
              <div className="flex gap-3 text-xs">
                <button type="button" className="underline text-gray-500" onClick={() => setUnchecked([])}>
                  <T _str="all" swc />
                </button>
                <button type="button" className="underline text-gray-500" onClick={() => setUnchecked(steps.map((step) => step.key))}>
                  <T _str="none" swc />
                </button>
              </div>
              {steps.map((step) => {
                const result = results.find((one) => one.key === step.key);
                return (
                  <label key={step.key} className="flex items-center gap-3 py-1 text-sm cursor-pointer">
                    <input type="checkbox" checked={!unchecked.includes(step.key)} onChange={() => toggle(step.key)} />
                    <span className="grow">{step.label}</span>
                    {result && (
                      <span className={classNames('text-xs', result.ok ? 'text-gray-500' : 'text-error-red-600 dark:text-error-red-400')}>
                        {result.ok ? `${result.result ?? 'ok'} · ${result.durationMs} ms` : result.error}
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
            <div>
              <Button variant="superAdmin" size="sm" onClick={onRun} loading={running} disabled={!chosen.length}>
                <T _str="run the job" swc />
              </Button>
            </div>
          </Section>

          <Section title={<T _str="Odoo subscription jobs" swc />}>
            <span className="text-xs text-gray-500">
              <T _str="Odoo's own scheduled actions, run now. They run on Odoo's real date, not the one above. A copied database has them switched off, which does not stop a manual run." />
            </span>
            {tools.odooCrons.length === 0 ? (
              <span className="text-sm text-gray-500">
                <T _str="No Odoo jobs found. Odoo may be off, or the API user may not see scheduled actions." />
              </span>
            ) : (
              tools.odooCrons.map((cron) => (
                <div key={cron.id} className="flex items-center gap-3 text-sm">
                  <span className="grow">{cron.name}</span>
                  <span className="text-xs text-gray-500">{cron.active ? cron.nextCall : <T _str="off" swc />}</span>
                  <Button variant="tertiary" size="xs" onClick={() => onRunCron(cron.id)} loading={runningCron === cron.id}>
                    <T _str="run now" swc />
                  </Button>
                </div>
              ))
            )}
          </Section>

          <Section title={<T _str="renew an Odoo subscription" swc />}>
            <span className="text-xs text-gray-500">
              <T _str="Sets the next invoice date of the organisation's linked Odoo subscription to today. Then run the recurring invoice job above to renew it." />
            </span>
            <div className="flex items-center gap-2">
              <Input className="w-80" placeholder="organisation id" value={organisationId} onChange={(event) => setOrganisationId(event.target.value)} />
              <Button variant="superAdmin" size="sm" onClick={onMakeDue} loading={makingDue} disabled={!organisationId.trim()}>
                <T _str="make it due today" swc />
              </Button>
            </div>
          </Section>
        </div>
      )}
    </div>
  );
};
export const DebugTools = memo(DebugTools_);
